import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Paneset, Pane, Loading, MessageBanner } from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';
import { useQueryClient } from 'react-query';
import { useStartRevalidate, useCurrentRevalidateJob } from '../hooks/useRevalidateJob';
import { useLatestRun, useRunDetail, useTrend, useLatestCorpusChecks, useRulesetStatus, } from '../api/useQuality';
import { TERMINAL_JOB_STATES } from '../types/validation';
import { isTimeoutError } from '../utils/errorKind';
import { ValidationSection } from '../components/Quality/ValidationSection';
import { SummaryStrip } from '../components/Quality/SummaryStrip';
import { CategoryPanels } from '../components/Quality/CategoryPanels';
import { RuleFailureTable } from '../components/Quality/RuleFailureTable';
import { QualityTrendChart } from '../components/Quality/QualityTrendChart';
import { CorpusChecksPanel } from '../components/Quality/CorpusChecksPanel';
import { CorpusCheckFindings } from '../components/Quality/CorpusCheckFindings';
var BASE_PATH = '/czna-authorities';
/** Capability that gates both run buttons (reuses AV-01 revalidate.execute). */
var RUN_CAPABILITY = 'czna-authorities.validation.revalidate.execute';
/**
 * AV-03 corpus-quality dashboard ("Kvalita korpusu", CZNA-330).
 *
 * <p>Reporting + navigation layer over the AV-01 revalidation engine: it starts
 * a corpus revalidation (reusing the AV-01 job), shows live progress, and once a
 * run completes renders the persisted snapshot — summary strip, three category
 * panels, the top-failing-rules table (drill-through to the records list), and a
 * quality trend chart over previous runs.
 *
 * <p>Read-only for cataloging-supervisor / cataloger (no "Spustit" button); the
 * button is gated on {@link RUN_CAPABILITY}.
 *
 * <p>CZNA-1217 adds "Kontroly korpusu" — checks of relations between records, run as a
 * job of scope CORPUS_CHECKS — and their findings list at {@code ?check=CODE}, so the list
 * has a URL of its own and the back button returns to the dashboard.
 *
 * <p>CZNA-1407 puts those checks first and folds the validation run under them: what NK
 * fixes in Aleph is the checks' findings, the validation figures are the second question.
 * Each section has its own date and run button; one lock serves both.
 *
 * <p>CZNA-1620: a rule change recomputes nothing by itself (AV-01 A1). When the rules changed
 * after the last completed full revalidation, the validation section says so with the time of
 * the change and offers the recompute (the same scope-ALL start) — it never starts it itself.
 */
var CorpusQuality = function () {
    var _a, _b, _c, _d, _e, _f;
    var history = useHistory();
    var location = useLocation();
    var checkCode = new URLSearchParams(location.search).get('check');
    var stripes = useStripes();
    var queryClient = useQueryClient();
    var canRun = stripes.hasPerm(RUN_CAPABILITY);
    // dataUpdatedAt moves on every completed poll, also when the answer is the same null.
    var _g = useCurrentRevalidateJob(), currentJob = _g.data, currentJobPolledAt = _g.dataUpdatedAt;
    var latest = useLatestRun();
    var detail = useRunDetail((_a = latest.data) === null || _a === void 0 ? void 0 : _a.id);
    var trend = useTrend();
    var corpusChecks = useLatestCorpusChecks();
    var rulesetStatus = useRulesetStatus();
    // The job this page watches: the active one the last poll reported, or the one it has just
    // started itself (see trackStartedJob).
    var trackedJobRef = useRef(null);
    var lastTerminalRef = useRef(null);
    // CZNA-1620: a job started here is tracked from its ack, not only once a poll has seen it
    // running — a short run can start and finish between two polls, and then no poll ever
    // reports it; the next poll's null (or another job) still ends the tracked one.
    var trackStartedJob = function (ack) {
        trackedJobRef.current = ack.jobId;
    };
    var _h = useState(null), startError = _h[0], setStartError = _h[1];
    // CZNA-1625 — a start with no answer in time may have started the job: never say "failed".
    var start = useStartRevalidate({
        onSuccess: trackStartedJob,
        onError: function (err) { return setStartError(isTimeoutError(err)
            ? 'ui-czna-authorities.error.timeout'
            : 'ui-czna-authorities.corpusQuality.run.startError'); },
    });
    var startChecks = useStartRevalidate({
        onSuccess: trackStartedJob,
        onError: function (err) { return setStartError(isTimeoutError(err)
            ? 'ui-czna-authorities.error.timeout'
            : 'ui-czna-authorities.corpusQuality.checks.startError'); },
    });
    // When a job this page watched has ended, refresh the persisted snapshot, detail, trend,
    // corpus checks and the ruleset status so the dashboard re-paints into the final state
    // (AV-03 step 4). GET /revalidate/current answers only for a QUEUED or RUNNING job and
    // goes to 204 (null) the moment it finishes, so the end shows as the tracked job
    // disappearing — or being replaced by another one between two polls (CZNA-1620). A job
    // reported in a terminal state is handled too, should the endpoint ever return one. It runs
    // on every poll (currentJobPolledAt), because a job tracked from its ack may end while the
    // answer stays null from one poll to the next.
    useEffect(function () {
        var job = currentJob !== null && currentJob !== void 0 ? currentJob : null;
        var activeJobId = job && !TERMINAL_JOB_STATES.has(job.state) ? job.jobId : null;
        var trackedJobEnded = trackedJobRef.current !== null && trackedJobRef.current !== activeJobId;
        var reportedTerminal = !!job && TERMINAL_JOB_STATES.has(job.state)
            && lastTerminalRef.current !== job.jobId;
        trackedJobRef.current = activeJobId;
        if (reportedTerminal && job)
            lastTerminalRef.current = job.jobId;
        if (trackedJobEnded || reportedTerminal) {
            queryClient.invalidateQueries(['quality-latest-run']);
            queryClient.invalidateQueries(['quality-runs']);
            queryClient.invalidateQueries(['quality-run-detail']);
            queryClient.invalidateQueries(['quality-trend']);
            queryClient.invalidateQueries(['quality-ruleset-status']);
            queryClient.invalidateQueries(['corpus-checks-latest']);
            queryClient.invalidateQueries(['corpus-check-findings']);
        }
    }, [currentJob, currentJobPolledAt, queryClient]);
    var anyRunning = !!currentJob && !TERMINAL_JOB_STATES.has(currentJob.state);
    // The corpus checks share the revalidation lock but have no per-record progress, so
    // their job shows in their own panel, not as the validation run's progress bar.
    var checksRunning = anyRunning && (currentJob === null || currentJob === void 0 ? void 0 : currentJob.scope) === 'CORPUS_CHECKS';
    var isRunning = anyRunning && !checksRunning;
    // Until the started job shows up in the poll, a start in flight holds the lock too.
    var anyStarting = start.isLoading || startChecks.isLoading;
    var handleStart = function () {
        setStartError(null);
        start.mutate({ scope: 'ALL' });
    };
    var handleStartChecks = function () {
        setStartError(null);
        startChecks.mutate({ scope: 'CORPUS_CHECKS' });
    };
    var handleShowFindings = function (code) {
        history.push("".concat(BASE_PATH, "/corpus-quality?check=").concat(encodeURIComponent(code)));
    };
    var handleDrillToRule = function (code) {
        // Reuse the records-list issue-code filter (AV-01 F-5 / AV-03 drill-down).
        history.push("".concat(BASE_PATH, "/authorities?issueCode=").concat(encodeURIComponent(code)));
    };
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.nav.corpusQuality" }), "data-test-corpus-quality": true },
            React.createElement("div", { style: { padding: '18px 22px', maxWidth: '980px' } },
                React.createElement("p", { style: { color: '#5d6b7a', fontSize: '13.5px', margin: '0 0 16px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.subtitle" })),
                startError ? (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setStartError(null); } },
                    React.createElement(FormattedMessage, { id: startError }))) : null,
                checkCode ? (React.createElement(CorpusCheckFindings, { key: checkCode, run: (_b = corpusChecks.data) !== null && _b !== void 0 ? _b : null, isRunLoading: corpusChecks.isLoading, isRunError: corpusChecks.isError, checkCode: checkCode, onBack: function () { return history.push("".concat(BASE_PATH, "/corpus-quality")); } })) : (React.createElement(React.Fragment, null,
                    React.createElement(CorpusChecksPanel, { run: (_c = corpusChecks.data) !== null && _c !== void 0 ? _c : null, isLoading: corpusChecks.isLoading, isError: corpusChecks.isError, isRunning: checksRunning, isOtherRunning: isRunning, canRun: canRun, isStarting: anyStarting, onStart: handleStartChecks, onShowFindings: handleShowFindings }),
                    React.createElement(ValidationSection, { latest: (_d = latest.data) !== null && _d !== void 0 ? _d : null, isLoading: latest.isLoading, isError: latest.isError, rulesetStatus: (_e = rulesetStatus.data) !== null && _e !== void 0 ? _e : null, isRulesetStatusError: rulesetStatus.isError, job: isRunning ? currentJob !== null && currentJob !== void 0 ? currentJob : null : null, isRunning: isRunning, isOtherRunning: checksRunning, canRun: canRun, isStarting: anyStarting, onStart: handleStart },
                        latest.isLoading ? (React.createElement("div", { style: { padding: '24px 0' } },
                            React.createElement(Loading, null))) : null,
                        !latest.isLoading && !latest.data ? (React.createElement(MessageBanner, { type: "default", "data-test-quality-empty": true },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.empty" }))) : null,
                        latest.data ? (React.createElement(React.Fragment, null,
                            React.createElement(SummaryStrip, { summary: latest.data }),
                            detail.isError ? (React.createElement(MessageBanner, { type: "error" },
                                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.detail.loadError" }))) : null,
                            detail.data ? (React.createElement(React.Fragment, null,
                                React.createElement(CategoryPanels, { detail: detail.data, onDrillToRule: handleDrillToRule }),
                                React.createElement(RuleFailureTable, { rules: detail.data.topRules, onDrillToRule: handleDrillToRule }))) : null,
                            React.createElement(QualityTrendChart, { points: (_f = trend.data) !== null && _f !== void 0 ? _f : [], isLoading: trend.isLoading, isError: trend.isError }))) : null)))))));
};
export default CorpusQuality;
